import { useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Heart, X } from "lucide-react";
import { cn } from "@/lib/utils";

const GOLD = "#C6A56B";
const SLIDE = {
  enter: (d) => ({ x: d * 48, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (d) => ({ x: d * -48, opacity: 0 }),
};

// Penampil foto layar penuh. Geser kiri/kanan, panah keyboard, Esc untuk menutup.
// Ketuk dua kali pada foto untuk menyukai.
export default function Lightbox({ photos, index, direction = 0, onIndex, onClose, onLike }) {
  const p = photos[index];

  const go = useCallback(
    (d) => {
      const n = index + d;
      if (n >= 0 && n < photos.length) onIndex(n, d);
    },
    [index, photos.length, onIndex]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [go, onClose]);

  // Muat foto di kiri-kanannya lebih dulu supaya geseran terasa mulus
  useEffect(() => {
    [photos[index - 1], photos[index + 1]].forEach((x) => {
      if (x) new Image().src = x.url;
    });
  }, [index, photos]);

  if (!p) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Penampil foto"
      className="fixed inset-0 z-[70] flex flex-col bg-black/95 text-white"
      style={{ touchAction: "none" }}
    >
      <div className="flex items-center justify-between px-4 pb-2 pt-[max(1rem,env(safe-area-inset-top))]">
        <span className="font-sans text-xs tracking-widest text-white/70">
          {index + 1} / {photos.length}
        </span>
        <button type="button" onClick={onClose} aria-label="Tutup" className="flex h-11 w-11 items-center justify-center text-white/80 hover:text-white">
          <X className="h-6 w-6" />
        </button>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <AnimatePresence initial={false} custom={direction} mode="popLayout">
          <motion.img
            key={p.pid}
            src={p.url}
            alt=""
            custom={direction}
            variants={SLIDE}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.22, ease: "easeOut" }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.45}
            onDragEnd={(_, { offset, velocity }) => {
              if (offset.x < -70 || velocity.x < -500) go(1);
              else if (offset.x > 70 || velocity.x > 500) go(-1);
            }}
            onDoubleClick={() => !p.liked && onLike(p)}
            draggable={false}
            className="max-h-full max-w-full select-none object-contain"
          />
        </AnimatePresence>

        {index > 0 && (
          <button type="button" onClick={() => go(-1)} aria-label="Foto sebelumnya" className="absolute left-2 hidden h-12 w-12 items-center justify-center bg-white/10 hover:bg-white/20 sm:flex">
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}
        {index < photos.length - 1 && (
          <button type="button" onClick={() => go(1)} aria-label="Foto berikutnya" className="absolute right-2 hidden h-12 w-12 items-center justify-center bg-white/10 hover:bg-white/20 sm:flex">
            <ChevronRight className="h-6 w-6" />
          </button>
        )}
      </div>

      <div className="flex items-center justify-between gap-4 px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-3">
        <span className="min-w-0 truncate font-sans text-xs text-white/60">{p.by || ""}</span>
        <button
          type="button"
          onClick={() => onLike(p)}
          aria-pressed={p.liked}
          aria-label={p.liked ? "Batal suka" : "Suka foto ini"}
          className="flex h-11 items-center gap-2 px-3 font-sans text-sm"
        >
          <Heart className={cn("h-6 w-6 transition-transform", p.liked && "scale-110")} style={p.liked ? { fill: GOLD, color: GOLD } : undefined} />
          <span className="min-w-[1ch]">{p.likes > 0 ? p.likes : ""}</span>
        </button>
      </div>
    </div>,
    document.body
  );
}
